import { Component, Input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { isCheckListFilter } from '@app/modules/table/classes/column-filters/check-list/check-list-filter.class';
import { isDateRangeFilter } from '@app/modules/table/classes/column-filters/date-range/date-range-filter.class';
import { Filter } from '@app/modules/table/classes/filter.abstract';
import { isSelectFilter } from '@app/modules/table/classes/table-filters/select-filter.class';
import { PlatformModule } from '@app/platform.module';
import { fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ColumnFilterComponent } from '../../column-filter/column-filter.component';

const SHORT_DATE_FORMAT = 'M/D/YY';

@Component({
    selector: 'app-table-multi-filter',
    templateUrl: './multi-filter.template.html',
    styleUrls: ['./multi-filter.styles.scss'],
    standalone: true,
    imports: [PlatformModule, ColumnFilterComponent],
})
export class MultiFilterComponent {
    @Input() filters?: Filter[];

    @ViewChild(MatMenuTrigger) trigger?: MatMenuTrigger;

    readonly ROW_HEIGHT = '40px';

    // Once all filters are ready this flag is set to true in order to remove the need of looping through the filters endlessly
    private allFiltersReady = false;

    constructor() {
        if (document.getElementsByClassName('page-container')?.length) {
            // Ensures the mat-menu for the filter doesn't float around on screen when the user scrolls outside of the menu
            fromEvent(document.getElementsByClassName('page-container'), 'scroll')
                .pipe(takeUntilDestroyed(), throttleTime(1000)) // throttle to reduce the number of events passed through since scroll events are a continuous stream,
                .subscribe(() => {
                    if (this.trigger?.menuOpen) {
                        this.trigger.closeMenu();
                    }
                });
        }
    }

    get areFiltersReady(): boolean {
        if (this.allFiltersReady) {
            return this.allFiltersReady;
        }

        this.allFiltersReady = Boolean(this.filters?.every((filter) => filter.isReady));
        return this.allFiltersReady;
    }

    // Each filter type has different logic for displaying the active badge within the multi-filter
    getActiveFilterString(filter: Filter): string | undefined {
        // The checklist filter will show a badge with the number of boxes checked
        if (isCheckListFilter(filter)) {
            return filter.currentValues.size > 0 ? filter.currentValues.size.toString() : undefined;
        }
        // The date range filter shows a condensed date string for the date range entered
        if (isDateRangeFilter(filter)) {
            return filter.hasValues()
                ? `${filter.startDate?.format(SHORT_DATE_FORMAT)}-${filter.endDate?.format(SHORT_DATE_FORMAT)}`
                : undefined;
        }
        // The select filter can only ever select 1 item at a time, therefore it displays a "1" or nothing at all
        if (isSelectFilter(filter)) {
            return filter.selected?.value ? '1' : undefined;
        }

        return undefined;
    }

    // If any filters would display an active filter string, that signifies that a filter is active
    hasActiveFilters(): boolean {
        return Boolean(this.filters?.some(this.getActiveFilterString));
    }
}
